// utils/qapLayout.js — QAP (1601-EQ Quarterly Alphalist of Payees) DAT record
// layout.
//
// ┌──────────────────────────────────────────────────────────────────────────┐
// │ PROVISIONAL. Authorised by the product owner for DAT-B (2026-10-14) in   │
// │ place of a BIR-accepted QAP sample, which we do not have yet. Every rule │
// │ below is an OPEN QUESTION in docs/bir-references-qap/QAP_LAYOUT.md §4–5. │
// │                                                                          │
// │ This file is the ONLY place the QAP format is written down. To replace   │
// │ it: rewrite this file (same exports) from the accepted sample, replace   │
// │ utils/__fixtures__/qap_provisional.DAT, and nothing else changes —       │
// │ utils/qapDat.js holds no format rule of its own (qapDat.test.js checks). │
// └──────────────────────────────────────────────────────────────────────────┘
//
// Shape (Alphalist v7 structure, provisional):
//   Header  HQAP,H1601EQ,<TIN9>,<BRANCH4>,"<REGISTERED NAME>","<LAST>","<FIRST>","<MIDDLE>",<MM/YYYY>,<RDO>
//   Detail  D1,1601EQ,<SEQ>,<TIN9>,<BRANCH4>,"<CORP NAME>","<LAST>","<FIRST>","<MIDDLE>",<MM/YYYY>,<ATC>,<RATE>,<TAX BASE>,<TAX WITHHELD>
//   Control C1,1601EQ,<TIN9>,<BRANCH4>,<MM/YYYY>,<TOTAL TAX BASE>,<TOTAL TAX WITHHELD>
//   File    <TIN9><BRANCH4><MMYYYY>1601EQ.DAT — one per quarter
//
// Conventions shared with the accepted SAWT sample come from datCommon.js
// (CRLF + trailing CRLF, MM/YYYY, two-decimal amounts, unpadded sequence).
import {
  cleanDatName, stripDiacritics, formatCentavos, formatPeriod, formatFilenamePeriod, joinDatLines,
} from './datCommon';
import { formatBranch, DAT_BRANCH_WIDTH } from './branchCode';

export const QAP_LAYOUT_STATUS = 'PROVISIONAL';
export const QAP_LAYOUT_NOTE =
  'Provisional format — validate this file in BIR Alphalist Data Entry & Validation Module before submitting';

export const FORM_TYPE = '1601EQ';

const DELIMITER = ',';
const quote = (s) => `"${s}"`;

// Names: uppercase (cleanDatName), diacritics stripped (Ñ → N), then only
// A–Z, 0–9, space, "&", "." and "-" kept. Open question 8.
const NOT_ALLOWED_IN_NAME = /[^A-Z0-9 &.-]/g;
export const cleanName = (s) =>
  stripDiacritics(cleanDatName(s)).toUpperCase().replace(NOT_ALLOWED_IN_NAME, '').replace(/\s+/g, ' ').trim();

/** Branch code in records and the file name: 4 digits (D4); null blocks. */
export const formatQapBranch = (code) => formatBranch(code, DAT_BRANCH_WIDTH);

/** One file per quarter; the period written is the quarter's last month. */
export const quarterPeriod = (year, quarter) => ({ month: Number(quarter) * 3, year: String(year) });

/** Sequence: unpadded, from 1 (as in the SAWT sample). */
export const formatSeq = (index) => String(index + 1);

/** Rate in basis points → percent with 2 decimals (500 → "5.00"), integer arithmetic. */
export const formatRate = (rateBasisPoints) => formatCentavos(rateBasisPoints);

export const formatAmount = (centavos) => formatCentavos(centavos);

/**
 * The four name fields. Corporate: registered name, last/first/middle empty.
 * Individual: registered name empty, last/first/middle filled. Every field is
 * quoted, empty ones as "".
 */
function nameFields({ entityType, registeredName, lastName, firstName, middleName }) {
  return entityType === 'individual'
    ? [quote(''), quote(cleanName(lastName)), quote(cleanName(firstName)), quote(cleanName(middleName))]
    : [quote(cleanName(registeredName)), quote(''), quote(''), quote('')];
}

const record = (fields) => fields.join(DELIMITER);

/**
 * @param {{tin9, branch4, entityType, registeredName, lastName, firstName, middleName, rdoCode, period}} h
 */
export const headerRecord = (h) => record([
  'HQAP', `H${FORM_TYPE}`, h.tin9, h.branch4, ...nameFields(h), formatPeriod(h.period), String(h.rdoCode ?? '').trim(),
]);

/**
 * @param {{index, tin9, branch4, entityType, registeredName, lastName, firstName, middleName,
 *          period, atcCode, rateBasisPoints, taxBaseCentavos, taxWithheldCentavos}} d
 */
export const detailRecord = (d) => record([
  'D1', FORM_TYPE, formatSeq(d.index), d.tin9, d.branch4, ...nameFields(d), formatPeriod(d.period),
  String(d.atcCode).trim().toUpperCase(), formatRate(d.rateBasisPoints),
  formatAmount(d.taxBaseCentavos), formatAmount(d.taxWithheldCentavos),
]);

/** @param {{tin9, branch4, period, totalTaxBaseCentavos, totalTaxWithheldCentavos}} c */
export const controlRecord = (c) => record([
  'C1', FORM_TYPE, c.tin9, c.branch4, formatPeriod(c.period),
  formatAmount(c.totalTaxBaseCentavos), formatAmount(c.totalTaxWithheldCentavos),
]);

/** Records → file content (CRLF, trailing CRLF). */
export const fileContent = (records) => joinDatLines(records);

/** <TIN9><BRANCH4><MMYYYY>1601EQ.DAT (RMC 015-2025 Annex B; MM = quarter's last month is provisional). */
export const fileName = ({ tin9, branch4, period }) => `${tin9}${branch4}${formatFilenamePeriod(period)}${FORM_TYPE}.DAT`;
